import { Button, Popover, PopoverContent, PopoverTrigger, TOUCH } from "@nymbl/nos2-rules";
import { Info } from "lucide-react";
import { cn } from "@/lib/utils";
import type { IncentiveGoal } from "@/schemas/incentives";

/** Whether the metric has any definition text to show. */
function hasMetricInfo(goal: IncentiveGoal): boolean {
  return Boolean(goal.definition || goal.why || goal.formula);
}

/**
 * The metric's definition (msMetric: what it is, why it matters, formula), from an ⓘ beside the
 * goal. A popover, so it opens on a tap on phones; renders nothing when the metric has no text.
 */
export function MetricInfo({ goal }: { goal: IncentiveGoal }) {
  if (!hasMetricInfo(goal)) return null;
  const parts = [
    { label: "What", text: goal.definition },
    { label: "Why", text: goal.why },
    { label: "Formula", text: goal.formula },
  ].filter((p): p is { label: string; text: string } => Boolean(p.text));

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={cn(TOUCH, "size-7 shrink-0 text-muted-foreground hover:text-foreground")}
          aria-label={`${goal.goal}: definition`}
        >
          <Info className="size-3.5" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        aria-label={`${goal.goal} definition`}
        className="flex w-auto max-w-80 flex-col gap-2 p-3 text-sm"
      >
        {parts.map((p) => (
          <div key={p.label}>
            <div className="text-xs font-semibold text-muted-foreground">{p.label}</div>
            <div>{p.text}</div>
          </div>
        ))}
      </PopoverContent>
    </Popover>
  );
}
